import React, { FC, ReactElement, ReactNode, useState } from 'react';

import { Icon } from '@iconify/react';
import { Popover, styled } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import IconButton from '~/components/atoms/buttons/IconButton';
import FallbackLetterAvatar from '~/components/atoms/FallbackLetterAvatar';
import Logo from '~/components/atoms/Logo';
import SidebarItem from '~/components/atoms/SidebarItem';
import ButtonSuggestion from '~/components/layouts/MainLayout/components/ButtonSuggestion';
import InnerNavigation from '~/components/molecules/InnerNavigation';
import { AvatarType } from '~/components/molecules/LogoMessageWrapper';
import SupportModal from '~/components/molecules/SupportModal';
import { useModalContext } from '~/contexts/modal/ModalContext';
import { SideBarItemType } from '~/core/sidebar';
import { checkIsAbsolutePath, checkIsRouteMatch } from '~/helpers/routeMatcher';
import i18n from '~/i18n/config';
import { getLanguages } from '~/i18n/helpers';
import PrepaidSidebarPromo from '~/modules/prepaid/components/SidebarPromo';
import {
  PersonalInformationPath,
  SecurityPath,
  SubscriptionPath,
} from '~/modules/settings/constants';
import { SETTINGS_PROJECT_CONFIG } from '~/modules/settings/projects';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { colors } from '~/theme/colors';
import { CARD_TYPE, SUBSCRIPTION_STATUS } from '~/types/subscription';
import { checkIsSubUnlimited } from '~/utils/subscription';

import styles from './styles.module.scss';

const StyledPopover = styled(Popover)(() => ({
  '.MuiPopover-paper': {
    margin: 0,
  },
}));

type Props = {
  items: SideBarItemType[][];
  onClickClose: () => void;
};

const SidebarMobile: FC<Props> = ({ items, onClickClose }) => {
  const { t } = useTranslation('general');
  const { signOut, updateUserFields } = useActions();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOpenModal, handleCloseModal } = useModalContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const profile = useAppSelector((state) => state.profile);

  const isPrepaid =
    !profile.is_stripe_user &&
    profile.user_subscription.status === SUBSCRIPTION_STATUS.ACTIVE &&
    profile.user_subscription.is_trial &&
    profile.user_subscription.card_type === CARD_TYPE.PREPAID &&
    profile.backend_ab_tests['experiment-prepaid-cards'] !== 'a_control';

  const handleHelpClick = (): void => {
    analytics.trackEvent('navigation - open help section');

    handleOpenModal({
      onClose: () => analytics.trackEvent('navigation - close support modal'),
      component: ({ onClose }) => <SupportModal onClose={onClose} />,
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleLanguagesOpen = (): void => {
    analytics.trackEvent('navigation - open language settings');

    handleOpenModal({
      disableBackdropClick: true,
      paddingSize: '48px 16px 16px',
      closeButtonColor: colors.neutral_30,
      component: () => (
        <div>
          <InnerNavigation groups={languagesGroups} />
        </div>
      ),
    });
  };

  const handleLanguagesClose = (): void => {
    handleCloseModal();
  };

  const handleLanguageClick = async (lang: string): Promise<void> => {
    analytics.trackEvent('settings - chose language', {
      language: lang,
      previous_language: profile.platform_lang || i18n.instance.language || '',
    });

    handleLanguagesClose();
    handleMenuClose();

    if (profile.platform_lang !== lang) {
      i18n.instance.changeLanguage(lang);

      await updateUserFields({
        platform_lang: lang,
      });
    }
  };

  const settingsGroups: Pick<
    SideBarItemType,
    'key' | 'path' | 'getTitle' | 'Icon' | 'onClickCb'
  >[][] = [
    [
      {
        key: 'personal_information',
        path: PersonalInformationPath,
        getTitle: (t: TFunction): string =>
          SETTINGS_PROJECT_CONFIG.getPersonalInformationTitle(t),
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:person-outline" />
        ),
      },
      {
        key: 'security',
        path: SecurityPath,
        getTitle: (t: TFunction): string =>
          SETTINGS_PROJECT_CONFIG.getSecurityTitle(t),
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:key-outline" />
        ),
      },
      {
        key: 'subscription',
        path: SubscriptionPath,
        getTitle: (t: TFunction): string =>
          SETTINGS_PROJECT_CONFIG.getSubscriptionTitle(t),
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:card-membership-outline" />
        ),
      },
    ],
    [
      {
        key: 'language',
        path: '#',
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:translate-rounded" />
        ),
        getTitle: (t: TFunction): string =>
          t('general:app_language', 'App Language'),
        onClickCb: handleLanguagesOpen,
      },
    ],
    [
      {
        key: 'help',
        path: '#',
        Icon: (): ReactNode => <Icon icon="material-symbols:help-outline" />,
        getTitle: (t: TFunction): string => t('help', 'Help'),
        onClickCb: (): void => handleHelpClick(),
      },
      {
        key: 'logout',
        path: '#',
        Icon: (): ReactNode => <Icon icon="material-symbols:logout" />,
        getTitle: (t: TFunction): string => t('logout', 'Logout'),
        onClickCb: (): void => {
          signOut();
        },
      },
    ],
  ];

  const languagesGroups: Pick<
    SideBarItemType,
    'key' | 'path' | 'getTitle' | 'Icon' | 'onClickCb'
  >[][] = getLanguages().map((lang) => {
    return [
      {
        key: 'lang',
        path: '#',
        Icon: () =>
          profile.platform_lang === lang.value ? (
            <Icon
              icon="material-symbols:check-rounded"
              className={styles.check_icon}
            />
          ) : (
            <div className={styles.check_icon} />
          ),
        getTitle: lang.getText,
        onClickCb: () => handleLanguageClick(lang.value),
      },
    ];
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <IconButton
          className={styles.sidebar__close_button}
          onClick={onClickClose}
          size="small"
        >
          <Icon icon="material-symbols:close" width="24" color="#45464F" />
        </IconButton>

        <div className={styles.header__logo_wrapper}>
          <div className={styles.header__logo}>
            <Logo />
          </div>

          {checkIsSubUnlimited(
            profile.is_stripe_user,
            profile.user_subscription.is_trial,
            profile.user_product.regularity,
          ) && <div className={styles.header__unlimited_label}>Unlimited</div>}
        </div>
      </div>

      <aside className={styles.sidebar}>
        <div onClick={handleMenuOpen} className={styles.sidebar__account_row}>
          <div className={styles.sidebar__flex_container}>
            <FallbackLetterAvatar
              logo_src={profile.avatar}
              className={styles.sidebar__account_row__avatar}
              variant={AvatarType.USER}
            >
              {profile.name.charAt(0)}
            </FallbackLetterAvatar>

            <p className={styles.sidebar__account_row__title}>{profile.name}</p>
          </div>

          <Icon
            className={styles.sidebar__account_row__icon}
            icon="material-symbols:unfold-more"
          />
        </div>

        <StyledPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            style: { width: '268px' },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <InnerNavigation groups={settingsGroups} />
        </StyledPopover>

        <div className={styles.sidebar__title}>{t('features', 'Features')}</div>

        <div className={styles.sidebar__items}>
          {items.map((items, i) => (
            <div key={i}>
              {items.map(
                ({
                  path,
                  getRedirectPath,
                  Icon,
                  getTitle,
                  getLabel,
                  onClickCb,
                }) => (
                  <SidebarItem
                    isActive={checkIsRouteMatch(path, location.pathname)}
                    key={path}
                    Icon={Icon}
                    onClick={(): void => {
                      onClickCb && onClickCb();
                      const redirectPath = getRedirectPath && getRedirectPath();
                      if (redirectPath && checkIsAbsolutePath(redirectPath)) {
                        window.setTimeout(() => {
                          // timeout for correct working analytics
                          window.location.href = redirectPath;
                        }, 100);
                      } else {
                        navigate(redirectPath || path);
                      }

                      onClickClose();
                    }}
                    title={getTitle(t)}
                    {...(getLabel ? { label: getLabel(t) } : {})}
                  />
                ),
              )}
            </div>
          ))}
        </div>

        {isPrepaid && (
          <div className={styles.prepaid}>
            <PrepaidSidebarPromo />
          </div>
        )}

        <div className={styles.buttons_panel}>
          <ButtonSuggestion />
        </div>
      </aside>
    </div>
  );
};

export default SidebarMobile;
